import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Milestone, Plus, Star, Trophy, Lightbulb, Target, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatUKDate, formatUKLongDate } from "@/lib/dateUtils";

interface ClientJourneyMilestonesProps {
  clientId: string;
  clinicianId: string;
}

const MILESTONE_TYPES = [
  { value: 'breakthrough', label: 'Breakthrough', icon: Sparkles },
  { value: 'goal_achieved', label: 'Goal Achieved', icon: Target },
  { value: 'assessment_improvement', label: 'Assessment Improvement', icon: Trophy },
  { value: 'personal_insight', label: 'Personal Insight', icon: Lightbulb },
  { value: 'behavioural_change', label: 'Behavioural Change', icon: Star },
  { value: 'session_milestone', label: 'Session Milestone', icon: Milestone },
  { value: 'custom', label: 'Custom', icon: Star }
];

export function ClientJourneyMilestones({ clientId, clinicianId }: ClientJourneyMilestonesProps) {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    title: '',
    description: '',
    milestone_type: 'breakthrough',
    milestone_date: new Date().toISOString().split('T')[0],
    significance_rating: 3,
    shared_with_client: false
  });

  // Fetch milestones
  const { data: milestones, isLoading } = useQuery({
    queryKey: ['client-milestones', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_journey_milestones')
        .select('*')
        .eq('client_id', clientId)
        .order('milestone_date', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Add milestone
  const addMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('client_journey_milestones')
        .insert({
          client_id: clientId,
          clinician_id: clinicianId,
          ...newMilestone
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-milestones', clientId] });
      setShowAddForm(false);
      setNewMilestone({
        title: '',
        description: '',
        milestone_type: 'breakthrough',
        milestone_date: new Date().toISOString().split('T')[0],
        significance_rating: 3,
        shared_with_client: false
      });
      toast.success("Milestone recorded");
    },
    onError: () => {
      toast.error("Failed to add milestone");
    }
  });

  // Toggle share with client
  const toggleShareMutation = useMutation({
    mutationFn: async ({ id, shared }: { id: string; shared: boolean }) => {
      const { error } = await supabase
        .from('client_journey_milestones')
        .update({ shared_with_client: shared })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-milestones', clientId] });
    }
  });

  const getMilestoneIcon = (type: string) => {
    const milestone = MILESTONE_TYPES.find(m => m.value === type);
    const Icon = milestone?.icon || Star;
    return <Icon className="h-5 w-5" />;
  };

  const getMilestoneColour = (type: string) => {
    switch (type) {
      case 'breakthrough': return 'text-purple-600 bg-purple-500/20';
      case 'goal_achieved': return 'text-green-600 bg-green-500/20';
      case 'assessment_improvement': return 'text-blue-600 bg-blue-500/20';
      case 'personal_insight': return 'text-yellow-600 bg-yellow-500/20';
      case 'behavioural_change': return 'text-orange-600 bg-orange-500/20';
      default: return 'text-primary bg-primary/20';
    }
  };

  const renderSignificanceStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`h-3 w-3 ${
              star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Milestone className="h-5 w-5 text-primary" />
            Journey Milestones
          </CardTitle>
          <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Record Milestone
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record a Milestone</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title</label>
                  <Input
                    value={newMilestone.title}
                    onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                    placeholder="e.g., First Panic Attack Managed Successfully"
                    className="bg-background/50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {MILESTONE_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <Button
                          key={type.value}
                          variant={newMilestone.milestone_type === type.value ? "default" : "outline"}
                          size="sm"
                          className="justify-start"
                          onClick={() => setNewMilestone({ ...newMilestone, milestone_type: type.value })}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {type.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    value={newMilestone.description}
                    onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                    placeholder="What made this moment significant..."
                    className="bg-background/50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Date</label>
                    <Input
                      type="date"
                      value={newMilestone.milestone_date}
                      onChange={(e) => setNewMilestone({ ...newMilestone, milestone_date: e.target.value })}
                      className="bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Significance (1-5)</label>
                    <div className="flex gap-1 pt-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setNewMilestone({ ...newMilestone, significance_rating: star })}
                        >
                          <Star
                            className={`h-6 w-6 ${
                              star <= newMilestone.significance_rating 
                                ? 'text-yellow-500 fill-yellow-500' 
                                : 'text-muted-foreground/30'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="shareWithClient"
                    checked={newMilestone.shared_with_client}
                    onChange={(e) => setNewMilestone({ ...newMilestone, shared_with_client: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="shareWithClient" className="text-sm">
                    Share this milestone with client
                  </label>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => addMutation.mutate()}
                    disabled={!newMilestone.title || addMutation.isPending}
                  >
                    Record Milestone
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading milestones...</p>
        ) : milestones?.length === 0 ? (
          <div className="text-center py-8">
            <Milestone className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No milestones recorded</p>
            <p className="text-sm text-muted-foreground mb-4">
              Capture breakthroughs and achievements along the therapeutic journey
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border/50" />

            <div className="space-y-4">
              {milestones?.map(milestone => (
                <div key={milestone.id} className="relative pl-14">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-4 w-5 h-5 rounded-full flex items-center justify-center ${getMilestoneColour(milestone.milestone_type)}`}
                  >
                    {getMilestoneIcon(milestone.milestone_type)}
                  </div>

                  <div className="p-4 rounded-lg bg-muted/30 border border-border/30">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{milestone.title}</h4>
                        <p className="text-xs text-muted-foreground">
                          {formatUKLongDate(new Date(milestone.milestone_date))}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {renderSignificanceStars(milestone.significance_rating)}
                        <Badge variant="outline" className="text-xs">
                          {MILESTONE_TYPES.find(t => t.value === milestone.milestone_type)?.label}
                        </Badge>
                      </div>
                    </div>

                    {milestone.description && (
                      <p className="text-sm text-muted-foreground mb-2">{milestone.description}</p>
                    )}

                    <div className="flex items-center justify-between">
                      <Badge
                        variant={milestone.shared_with_client ? "default" : "outline"}
                        className="text-xs cursor-pointer"
                        onClick={() => toggleShareMutation.mutate({
                          id: milestone.id,
                          shared: !milestone.shared_with_client
                        })}
                      >
                        {milestone.shared_with_client ? 'Shared with client' : 'Not shared'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
